import React, { useState, useEffect } from 'react';
import { categoryService } from '../../services/categoryService';
import DataTable from '../components/common/DataTable';
import FormModal from '../components/common/FormModal';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import ToastNotification from '../components/common/ToastNotification';
import CategoryForm from '../components/Categories/CategoryForm';
import InfoModal from '../components/common/InfoModal';


function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAll();
      setCategories(response.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleAdd = () => {
    setSelectedCategory(null);
    setShowFormModal(true);
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setShowInfoModal(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setShowFormModal(true);
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      if (selectedCategory) {
        await categoryService.update(selectedCategory.id, formData);
        showNotification('Category updated successfully');
      } else {
        await categoryService.create(formData);
        showNotification('Category created successfully');
      }
      setShowFormModal(false);
      setSelectedCategory(null);
      await fetchCategories();
    } catch (err) {
      showNotification(err.message, 'danger');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await categoryService.delete(selectedCategory.id);
      showNotification('Category deleted successfully');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      await fetchCategories();
    } catch (err) {
      showNotification(err.message, 'danger');
    }
  };

  const columns = [
    { 
      key: 'id', 
      header: 'ID',
      render: (value) => <span style={{ fontWeight: '500' }}>#{value}</span>
    },
    { 
      key: 'name', 
      header: 'Name',
      render: (value) => <span style={{ fontWeight: '500' }}>{value}</span>
    },
    { 
      key: 'isTutorials', 
      header: 'Type',
      render: (value) => (
        <span className={`admin-crud-badge ${value ? 'admin-crud-badge-success' : 'admin-crud-badge-info'}`}>
          {value ? '📘 Tutorials' : '📝 Blogs'}
        </span>
      )
    },
    { 
      key: 'isDeleted', 
      header: 'Status',
      render: (value) => (
        <span className={`admin-crud-badge ${!value ? 'admin-crud-badge-success' : 'admin-crud-badge-danger'}`}>
          {!value ? '✅ Active' : '❌ Inactive'}
        </span>
      )
    },
    { 
      key: 'createdAt', 
      header: 'Created',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ];

  const infoFields = [
    { key: 'id', label: 'Category ID' },
    { key: 'name', label: 'Category Name' },
    { 
      key: 'isTutorials', 
      label: 'Type',
      render: (value) => value ? 'Tutorials' : 'Blogs',
      badge: (value) => value ? 'success' : 'info'
    },
    { 
      key: 'isDeleted', 
      label: 'Status',
      render: (value) => !value ? 'Active' : 'Inactive',
      badge: (value) => !value ? 'success' : 'danger'
    },
    { 
      key: 'createdAt', 
      label: 'Created Date',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    },
    { 
      key: 'updatedAt', 
      label: 'Last Updated',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    }
  ];

  if (loading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        {error && (
          <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
            <div className="admin-crud-toast-content admin-crud-toast-danger">
              <div className="admin-crud-toast-header">
                <strong>Error</strong>
                <button onClick={() => setError(null)}>✕</button>
              </div>
              <div className="admin-crud-toast-body">{error}</div>
            </div>
          </div>
        )}

        <DataTable
          title="Categories Management"
          columns={columns}
          data={categories}
          onAdd={handleAdd}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          addButtonText="Add New Category"
          searchPlaceholder="Search categories by name or ID..."
        />

        {/* Info/View Modal */}
        <InfoModal
          show={showInfoModal}
          onHide={() => {
            setShowInfoModal(false);
            setSelectedCategory(null);
          }}
          title="Category Details"
          data={selectedCategory}
          fields={infoFields}
        />

        {/* Form Modal */}
        <FormModal
          show={showFormModal}
          onHide={() => {
            setShowFormModal(false);
            setSelectedCategory(null);
          }}
          title={selectedCategory ? 'Edit Category' : 'Create New Category'}
        >
          <CategoryForm
            initialData={selectedCategory}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowFormModal(false);
              setSelectedCategory(null);
            }}
            isSubmitting={isSubmitting}
          />
        </FormModal>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedCategory(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Category"
          message={`Are you sure you want to delete "${selectedCategory?.name}"? This action cannot be undone.`}
        />

        {/* Toast Notification */}
        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
}

export default CategoryList;